/**
 * The ClinSpaceXR design language: one set of tokens for the entry portal, the lobby, every
 * canvas panel, the wrist menu, the study rooms and the 2D pages (which mirror these values
 * as CSS custom properties). See docs/lobby-redesign.md.
 */

export const TOKENS = {
  void: '#06080c',
  charcoal: '#0b0f15',
  graphite: '#121822',
  slate: '#1a2230',
  steel: '#273242',
  silver: '#a9b6c6',
  mist: '#d6dee8',
  white: '#f2f6fb',
  cyan: '#3fd6ff',
  cyanSoft: '#9beaff',
  cyanDeep: '#1593b8',
  blue: '#3d7bff',
  amber: '#f2b44b',
  rose: '#ff5d73',
  green: '#3fd69a',
  violet: '#a58bff',
  grey: '#7f8a98',
} as const;

/** Display face for titles, codes and big numbers; body face for everything else. */
export const DISPLAY_FONT = '"Sora Variable", "Inter Variable", "Segoe UI", system-ui, sans-serif';
export const BODY_FONT = '"Inter Variable", "Inter", "Segoe UI", Roboto, system-ui, sans-serif';

/**
 * Glass panel background: top and bottom of the vertical gradient, the hovered stops, edge
 * and glow. Dark values here; `setUiMode` in dashboards/charts.ts swaps them for the chamber
 * study rooms.
 */
export const GLASS = {
  top: 'rgba(24,32,45,0.96)',
  bottom: 'rgba(10,14,20,0.96)',
  hoverTop: 'rgba(34,46,63,0.97)',
  hoverBottom: 'rgba(15,21,30,0.97)',
  edge: 'rgba(155,234,255,0.22)',
  highlight: 'rgba(63,214,255,0.85)',
  highlightEdge: 'rgba(63,214,255,0.9)',
  highlightLine: 'rgba(155,234,255,1)',
  inner: 'rgba(255,255,255,0.04)',
};

/**
 * A study colour lifted for light: the same hue, saturated and bright enough to glow on
 * charcoal. Studies may carry any colour (the generated ones were chosen for light rooms);
 * every glow, pathway and gateway uses this, text and fills may use the original.
 */
export function luminous(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  let r = ((n >> 16) & 255) / 255;
  let g = ((n >> 8) & 255) / 255;
  let b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const d = max - min;
  if (d > 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
    if (h < 0) h += 1;
  }
  const l = Math.max(0.62, (max + min) / 2);
  const sat = Math.max(0.7, d === 0 ? 0 : d / (1 - Math.abs(max + min - 1)));
  const q = l < 0.5 ? l * (1 + sat) : l + sat - l * sat;
  const pp = 2 * l - q;
  const hue = (t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return pp + (q - pp) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return pp + (q - pp) * (2 / 3 - t) * 6;
    return pp;
  };
  r = hue(h + 1 / 3);
  g = hue(h);
  b = hue(h - 1 / 3);
  const to = (x: number): string =>
    Math.round(Math.min(1, Math.max(0, x)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** `#rrggbb` to a three.js-style number. */
export function hexNumber(hex: string): number {
  return parseInt(hex.slice(1), 16);
}

/** Waits for the shipped faces so canvas panels never draw with a fallback font. */
export async function loadFonts(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  await Promise.all([
    document.fonts.load('600 32px "Sora Variable"'),
    document.fonts.load('700 32px "Sora Variable"'),
    document.fonts.load('500 16px "Inter Variable"'),
    document.fonts.load('700 16px "Inter Variable"'),
  ]).catch(() => undefined);
}
